import React, { useCallback } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useJsApiLoader } from '@react-google-maps/api'
import { useTranslation } from 'react-i18next'
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete'

import { TextInput } from '@/components/widget-components'
import { ProvinceSelector } from '@/components/Select/ProvinceSelector'
import { type AddressFormValues, formatPostalCode } from './address'
import { FieldError, TextField } from '@/components/FormField'

const GOOGLE_MAPS_LIBRARIES: ['places'] = ['places']

const PLACES_SEARCH_OPTIONS = {
  componentRestrictions: { country: 'ca' },
  types: ['address'],
}

export const AddressFields = React.memo(function AddressFields({
  secondaryColumns,
}: {
  secondaryColumns: 2 | 4
}) {
  const { t } = useTranslation()
  const { control, setValue, setError, clearErrors } =
    useFormContext<AddressFormValues>()

  const secondaryGridClassName =
    secondaryColumns === 4
      ? 'grid grid-cols-4 gap-[15px] items-start'
      : 'grid grid-cols-2 gap-[15px] items-start'

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const handleSelectAddress = useCallback(
    (address: string) => {
      clearErrors('root')
      geocodeByAddress(address)
        .then((results) => {
          const addrObj: Record<string, string> = Object.fromEntries(
            results[0].address_components.flatMap((component) =>
              component.types.map((type): [string, string] => [
                type,
                type === 'administrative_area_level_1'
                  ? component.short_name
                  : component.long_name,
              ])
            )
          )
          const setField = (name: keyof AddressFormValues, value: string) =>
            setValue(name, value, { shouldValidate: true, shouldTouch: true })

          setField(
            'address1',
            `${addrObj.street_number ?? ''} ${addrObj.route ?? ''}`.trim()
          )
          setField('address2', addrObj.subpremise ?? '')
          setField('city', addrObj.locality ?? '')
          setField(
            'province',
            (addrObj.administrative_area_level_1 ?? '').toUpperCase()
          )
          setField('postal', formatPostalCode(addrObj.postal_code ?? ''))
        })
        .catch(() => {
          setValue('address1', address, { shouldValidate: true })
          setError('root', {
            message: t('kycRefresh.error.addressAutofillFailed'),
          })
        })
    },
    [clearErrors, setValue, setError, t]
  )

  return (
    <>
      {isLoaded ? (
        <Controller
          control={control}
          name="address1"
          render={({ field, fieldState }) => (
            <PlacesAutocomplete
              value={field.value}
              onChange={field.onChange}
              onSelect={handleSelectAddress}
              searchOptions={PLACES_SEARCH_OPTIONS}
            >
              {({
                getInputProps,
                suggestions,
                getSuggestionItemProps,
                loading,
              }) => (
                <div className="relative">
                  <TextInput
                    {...getInputProps({
                      id: 'input-street-address',
                      placeholder: t('personalInfo.streetAddress.placeholder'),
                      'aria-label': t('personalInfo.streetAddress.placeholder'),
                      'aria-invalid': !!fieldState.error,
                      'aria-describedby': fieldState.error
                        ? 'input-street-address-error'
                        : undefined,
                      className: 'location-search-input',
                      'data-testid': 'input-street-address',
                      onBlur: field.onBlur,
                      required: true,
                    })}
                  />
                  <div
                    className="absolute w-full z-50"
                    data-testid="text-suggestions-container"
                  >
                    {loading && (
                      <div
                        className="bg-white dark:bg-neutral-900 p-2 border-x border-pebble dark:border-white"
                        data-testid="text-loading-indicator"
                      >
                        {t('personalInfo.loading')}
                      </div>
                    )}
                    {suggestions.length > 0 && (
                      <div
                        className="bg-white dark:bg-neutral-900 border-x border-b border-pebble dark:border-white shadow-lg"
                        data-testid="text-suggestions-list"
                      >
                        {suggestions.map((suggestion) => {
                          const className = suggestion.active
                            ? 'bg-rock dark:bg-neutral-800 p-2 cursor-pointer'
                            : 'hover:bg-rock dark:hover:bg-neutral-800 p-2 cursor-pointer'

                          return (
                            <div
                              {...getSuggestionItemProps(suggestion, {
                                className,
                              })}
                              data-testid={`address-suggestion-${suggestion.index}`}
                            >
                              <span>{suggestion.description}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                  <FieldError
                    message={fieldState.error?.message}
                    id="input-street-address-error"
                  />
                </div>
              )}
            </PlacesAutocomplete>
          )}
        />
      ) : (
        <TextField<AddressFormValues>
          name="address1"
          placeholder={t('personalInfo.streetAddress.placeholder')}
          testId="input-street-address"
          required
        />
      )}

      <div className={secondaryGridClassName}>
        <TextField<AddressFormValues>
          name="address2"
          placeholder={t('personalInfo.unitNumber.placeholder')}
          testId="input-unit-number"
        />
        <TextField<AddressFormValues>
          name="city"
          placeholder={t('personalInfo.city.placeholder')}
          testId="input-form-city"
          required
        />
        <div className="[&_button]:!min-w-0 [&_button]:!mt-2.5 [&_button]:!h-8 [&_button]:!py-0 [&_button]:!items-center [&_button>div]:!top-0">
          <Controller
            control={control}
            name="province"
            render={({ field, fieldState }) => (
              <>
                <ProvinceSelector
                  province={field.value}
                  onSelectProvince={field.onChange}
                />
                <FieldError
                  message={fieldState.error?.message}
                  id="address-province-error"
                />
              </>
            )}
          />
        </div>
        <TextField<AddressFormValues>
          name="postal"
          placeholder={t('personalInfo.postalCode.placeholder')}
          testId="input-postal-code"
          required
          format={formatPostalCode}
        />
      </div>
    </>
  )
})
